import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './AssessmentResults.css';

/**
 * AssessmentResults Component
 *
 * Displays detailed results for a completed assessment.
 * Adapts display based on user role (candidate sees encouraging view, recruiter sees detailed metrics).
 */
const AssessmentResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [assessment, setAssessment] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadResults();
  }, [id]);

  const loadResults = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await assessmentsAPI.getResults(id);
      setAssessment(response.data.assessment);
      setResults(response.data.results);
    } catch (err) {
      console.error('[AssessmentResults] Error loading results:', err);
      setError(err.response?.data?.error || 'Failed to load assessment results');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0m 0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#27ae60'; // Green
    if (score >= 60) return '#f39c12'; // Orange
    return '#e74c3c'; // Red
  };

  const getScoreGrade = (score) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  const isRecruiter = user?.role === 'recruiter' || user?.role === 'admin';
  const isCandidate = user?.role === 'candidate';

  if (loading) {
    return (
      <div className="results-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-container">
        <div className="error-state">
          <h2>Error Loading Results</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="results-container">
        <div className="error-state">
          <h2>No Results Found</h2>
          <p>Results are not available for this assessment.</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="results-container">
      {/* Header */}
      <div className="results-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        <div className="header-content">
          <h1>{assessment.name}</h1>
          <p className="assessment-position">{assessment.position}</p>
          {assessment.status === 'expired' && (
            <p style={{ color: '#ef4444', marginTop: '0.5rem', fontSize: '0.875rem' }}>
              Assessment expired - Showing partial results
            </p>
          )}
        </div>
      </div>

      {/* Expired Assessment Notice */}
      {results?.isPartial && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          color: '#92400e'
        }}>
          <strong>Assessment Expired</strong>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
            {results.message || 'The assessment expired before completion. Results shown reflect the progress made up to the expiration time.'}
          </p>
        </div>
      )}

      {/* Overall Score Card */}
      {results && results.overallScore !== undefined && results.overallScore !== null ? (
        <div className="score-card">
          <div className="score-circle" style={{ borderColor: getScoreColor(results.overallScore) }}>
            <div className="score-value" style={{ color: getScoreColor(results.overallScore) }}>
              {results.overallScore}
            </div>
            <div className="score-label">Overall Score</div>
            <div className="score-grade">{getScoreGrade(results.overallScore)}</div>
          </div>

          <div className="score-status">
            {results.passed ? (
              <div className="status-passed">
                <div className="status-icon">✓</div>
                <div className="status-text">
                  <h2>Congratulations!</h2>
                  <p>You passed the assessment</p>
                </div>
              </div>
            ) : (
              <div className="status-failed">
                <div className="status-icon">✗</div>
                <div className="status-text">
                  <h2>Assessment Incomplete</h2>
                  <p>Minimum required: {assessment.scoring?.passThreshold}%</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : results?.isPartial ? (
        <div className="score-card">
          <div className="score-circle" style={{ borderColor: '#6b7280' }}>
            <div className="score-value" style={{ color: '#6b7280' }}>
              —
            </div>
            <div className="score-label">Partial Progress</div>
          </div>
          <div className="score-status">
            <div className="status-failed">
              <div className="status-text">
                <h2>Assessment Expired</h2>
                <p>View your progress below</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Results Grid */}
      <div className="results-grid">
        {/* Flags Performance */}
        <div className="result-card">
          <div className="card-header">
            <h3>Flags Performance</h3>
          </div>
          <div className="card-body">
            <div className="metric-row">
              <span className="metric-label">Submitted:</span>
              <span className="metric-value">{results?.flagsSubmitted || 0}</span>
            </div>
            <div className="metric-row">
              <span className="metric-label">Correct:</span>
              <span className="metric-value success">{results?.flagsCorrect || 0}</span>
            </div>
            <div className="metric-row">
              <span className="metric-label">Accuracy:</span>
              <span className="metric-value">
                {results?.flagsSubmitted > 0
                  ? Math.round((results.flagsCorrect / results.flagsSubmitted) * 100)
                  : 0}%
              </span>
            </div>
            {results?.isPartial && results?.totalChallenges && (
              <div className="metric-row">
                <span className="metric-label">Challenges Completed:</span>
                <span className="metric-value">{results.challengesCompleted || 0} / {results.totalChallenges}</span>
              </div>
            )}
          </div>
        </div>

        {/* Time Performance */}
        <div className="result-card">
          <div className="card-header">
            <h3>Time Performance</h3>
          </div>
          <div className="card-body">
            <div className="metric-row">
              <span className="metric-label">Time Spent:</span>
              <span className="metric-value">{formatTime(results?.timeSpent || 0)}</span>
            </div>
            {assessment.scheduling?.timeLimit && (
              <div className="metric-row">
                <span className="metric-label">Time Limit:</span>
                <span className="metric-value">{assessment.scheduling.timeLimit} min</span>
              </div>
            )}
            <div className="metric-row">
              <span className="metric-label">Efficiency:</span>
              <span className="metric-value">
                {assessment.scoring?.components?.timeEfficiency || 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Hints & Penalties */}
        {assessment.hints?.enabled && (
          <div className="result-card">
            <div className="card-header">
              <h3>Hints & Assistance</h3>
            </div>
            <div className="card-body">
              <div className="metric-row">
                <span className="metric-label">Hints Used:</span>
                <span className="metric-value">{results?.hintsUsed || 0}</span>
              </div>
              <div className="metric-row">
                <span className="metric-label">Penalty per Hint:</span>
                <span className="metric-value">{assessment.hints.penaltyPerHint || 0} pts</span>
              </div>
              <div className="metric-row">
                <span className="metric-label">Total Penalty:</span>
                <span className="metric-value penalty">-{results.totalPenalty || 0} pts</span>
              </div>
            </div>
          </div>
        )}

        {/* Scoring Breakdown (Recruiter View) */}
        {isRecruiter && (
          <div className="result-card">
            <div className="card-header">
              <h3>Scoring Breakdown</h3>
            </div>
            <div className="card-body">
              <div className="metric-row">
                <span className="metric-label">Flags Correct:</span>
                <span className="metric-value">{assessment.scoring?.components?.flagsCorrect || 0}%</span>
              </div>
              <div className="metric-row">
                <span className="metric-label">Time Efficiency:</span>
                <span className="metric-value">{assessment.scoring?.components?.timeEfficiency || 0}%</span>
              </div>
              <div className="metric-row">
                <span className="metric-label">Methodology:</span>
                <span className="metric-value">{assessment.scoring?.components?.methodology || 0}%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Candidate Information (Recruiter View) */}
      {isRecruiter && assessment.candidateId && (
        <div className="candidate-info-section">
          <h2>Candidate Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Name:</label>
              <span>{assessment.candidateId.name}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{assessment.candidateId.email}</span>
            </div>
            <div className="info-item">
              <label>Completed:</label>
              <span>{new Date(assessment.completedAt).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Encouraging Message for Candidates */}
      {isCandidate && (
        <div className="encouragement-section">
          <h2>Next Steps</h2>
          {results.passed ? (
            <p>
              Excellent work! Your performance on this assessment demonstrates strong skills in security
              analysis. The recruiting team will review your results and be in touch soon.
            </p>
          ) : (
            <p>
              Thank you for taking the time to complete this assessment. While the minimum threshold was
              not reached, we appreciate your effort. You may be contacted regarding next steps.
            </p>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="results-actions">
        {isCandidate && (
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        )}
        {isRecruiter && (
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/recruiter/assessments')}>
              Back to Assessments
            </button>
            <button className="btn btn-primary" onClick={() => window.print()}>
              Print Results
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AssessmentResults;
